import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AlertController, NavParams } from '@ionic/angular/standalone';
import { interval, Subject, timer, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StorageService } from '../../../core/services/storage.service';
import { AppConfig } from '../../../config/routes.config';
import { AlertService } from '@v-locker-app/v-locker-common';
import { TowerGroupDto } from 'src/app/api/models';
import { UserBookingsApiService, UserBookingSharesApiService } from 'src/app/api/services';
import { BookingStatus, StorageEnums } from 'src/shared/models/enums';
import { CustomBookingDTO } from 'src/shared/models/global.model';
import { BookingHelperService } from 'src/shared/services/helpers/booking-helper.service';
import { CustomSpinnerService } from 'src/shared/services/helpers/custom-spinner.service';
import { GeolocationService } from 'src/shared/services/helpers/geolocation.service';
import { HelperService } from 'src/shared/services/helpers/helper.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgClass, NgIf } from '@angular/common';
import { IconComponent } from "../../../../shared/components/elements/icon/icon.component";
import { IonButton } from "@ionic/angular/standalone";
import { ContextHelpComponent } from "../../../../shared/components/context-help/context-help.component";

@Component({
	selector: 'vl-door-status',
	templateUrl: './door-status.component.html',
	styleUrls: ['./door-status.component.scss'],
	imports: [IonButton, TranslatePipe, NgClass, NgIf, IconComponent, ContextHelpComponent],
})
export class DoorStatusComponent implements OnInit, OnDestroy {
	@Input() group: TowerGroupDto;
	@Input() booking: CustomBookingDTO;
	@Input() bookingId: number;
	@Input() towerStatus;
	@Input() doorAction: boolean = false;
	@Input() doorModal: HTMLIonModalElement;

	message: string;
	bottomMessage: string;
	facility: string;
	color: string;
	currentStatus: BookingStatus;
	stepsEnum = BookingStatus;
	routes = AppConfig.routes;
	showAbortBtn = false;
	abortIntervalExpired = 0;
	private notifier: Subject<any> = new Subject<any>();
	private statusNotifier: Subject<any> = new Subject<any>();
	private isOpenedWasUsed = false;
	private timeoutSubscription: Subscription;
	private previousStatus: BookingStatus;
	private previousMotionStatus: string;
	closeOption: 'keep-booking' | 'terminate-booking' = 'keep-booking';
	closeActionNumber: number;
	supportModal: HTMLIonModalElement;
	disabledButton: boolean = false;
	helpPageAlert: HTMLIonAlertElement;
	blockTimer: boolean = false;
	doorLabel: string;

	constructor(params: NavParams,
				private geolocationService: GeolocationService,
				private alertCtrl: AlertController,
				private translateService: TranslateService,
				private loader: CustomSpinnerService,
				private bookingHelper: BookingHelperService,
				private bookingService: UserBookingsApiService,
				private helperService: HelperService,
				private storage: StorageService,
				private shareService: UserBookingSharesApiService,
				private alertService: AlertService) {
		this.currentStatus = params.get('status');
		this.setMessages(this.currentStatus);
		this.previousStatus = this.currentStatus;
		this.previousMotionStatus = null;
	}

	async ngOnInit() {
		if (this.towerStatus === 'OPEN') {
			if (this.doorAction) {
				this.action(BookingStatus.DoorIsClosing);
				this.closeDoorAction();
			} else {
				this.handleTowerStatusChange(this.towerStatus);
			}
		} else {
			this.interval();
			await this.storage.setItem(StorageEnums.BookingStep, BookingStatus.DoorIsOpen);
			await this.storage.setItem(StorageEnums.DoorStatusOpened, 'yes');
		}
	}

	async ngOnDestroy() {
		await this.storage.removeItem(StorageEnums.DoorStatusOpened);
		this.stopTimeout();
	}

	private interval() {
		interval(3000)
			.pipe(takeUntil(this.notifier))
			.subscribe(() => {
				this.abortIntervalExpired += 5;
				if (this.abortIntervalExpired >= 15) {
					this.showAbortBtn = true;
				}
				if (this.booking.sharedWithMe) {
					this.checkStatusRequestForShare();
				} else {
					this.checkStatusRequest();
				}
			});
	}

	private startTimeout() {
		this.stopTimeout();
		this.timeoutSubscription = timer(30000).subscribe(() => this.onTimeout());
	}

	stopTimeout() {
		if (this.timeoutSubscription) {
			this.timeoutSubscription.unsubscribe();
		}
	}

	private resetTimeout() {
		if(!this.blockTimer) this.startTimeout();
	}

	private onTimeout() {
		this.blockTimer = true;
		this.stopTimeout();
		this.showHelpPageAlert();
	}

	async openDoorHelp() {
		this.helperService.checkRoute('modal-window/door-instructions');
	}

	async showHelpPageAlert() {
		this.helpPageAlert = await this.alertCtrl.create({
			cssClass: 'wide-alert-modal',
			header: this.translateService.instant('alerts.title'),
			message: this.translateService.instant('alerts.is-wrong'),
			mode: 'ios',
			buttons: [
				{
					text: this.translateService.instant('booking-steps.doors.show-help-page'),
					handler: async () => {
						await this.helperService.checkRoute('modal-window/door-status', true, this.group);
					}
				},
				{
					text: this.translateService.instant('buttons.wait'),
					handler: () => {
						this.blockTimer = false;
						this.resetTimeout();
						this.helpPageAlert.dismiss();
					}
				},
			]
		});
		this.helpPageAlert.onDidDismiss().then(({role}) => {
			if (role === 'backdrop') {
				this.blockTimer = false;
				this.resetTimeout();
			}
		});
		await this.helpPageAlert.present();
	}

	private checkStatusRequest() {
		this.bookingService.getBookingTowerInfo({ bookingId: this.bookingId })
			.pipe(takeUntil(this.statusNotifier))
			.subscribe(t => {
				this.doorLabel = t?.doorLabel;
				this.handleTowerStatusChange(t.status, t.motion);
			});
	}

	private handleTowerStatusChange(status, motionStatus = null) {
		this.setBottomMessage(motionStatus);
		if ((status !== this.previousStatus) || (motionStatus !== this.previousMotionStatus)) {
			this.resetTimeout();  // Reset timeout only if status or motion status changes
			this.previousStatus = status;
			this.previousMotionStatus = motionStatus;
		}

		if (status === 'OPEN' && !this.isOpenedWasUsed) {
			this.isOpenedWasUsed = true;
			this.action(BookingStatus.DoorIsOpen);
			// this.stopInterval(true);
			this.blockTimer = true;
			this.stopTimeout(); // Stop timeout when the door is fully opened
			this.closeAlert();
		} else if (status === 'CLOSED') {
			this.abort(status);
			this.blockTimer = true;
			this.stopTimeout(); // Stop timeout when the door is fully closed
		}
	}

	setBottomMessage(motionStatus: string) {
		const motionObj = {
			"ROTATING_RIGHT": this.translateService.instant('booking-steps.doors.motion.rotating'),
			"ROTATING_LEFT": this.translateService.instant('booking-steps.doors.motion.rotating'),
			"OPENING": this.translateService.instant('booking-steps.doors.motion.opening'),
			"CLOSING": this.translateService.instant('booking-steps.doors.motion.closing')
		}
		this.bottomMessage = motionObj[motionStatus];
	}

	private checkStatusRequestForShare() {
		this.shareService.getTowerInfo({ shareId: this.booking.shareId })
			.pipe(takeUntil(this.statusNotifier))
			.subscribe(t => {
				this.doorLabel = t?.doorLabel;
				this.setBottomMessage(t.motion);
				this.handleTowerStatusChange(t.status, t.motion);
			});
	}

	async closeDoorAction() {
		this.closeDoor();
	}

	public segmentChanged(type) {
		this.closeOption = type?.detail?.value;
	}

	private setMessages(status: BookingStatus) {
		switch (status) {
			case BookingStatus.DoorsIsOpening:
				this.color = 'blue';
				this.message = 'booking-steps.doors.door-is-opening';
				break;
			case BookingStatus.DoorIsOpen:
				this.bottomMessage = '';
				this.color = 'green';
				this.message = 'booking-steps.doors.door-is-open';
				break;
			case BookingStatus.DoorIsClosing:
				this.bottomMessage = '';
				this.color = 'red';
				this.message = 'booking-steps.doors.please-wait';
				break;
		}
	}

	action(status: BookingStatus) {
		this.currentStatus = status;
		this.setMessages(this.currentStatus);
	}

	closeDoor() {
		this.blockTimer = false;
		this.startTimeout(); // Start timeout when attempting to close the door
		this.disabledButton = true;
		this.checkDistance();
	}

	closeDoorRequest() {
		this.loader.show().then(() => {
			this.bookingService.closeBox({ bookingId: this.bookingId }).subscribe(r => {
				this.bookingHelper.removeBookingAtTheGate();
				this.loader.hide().then(() => {
					this.action(BookingStatus.DoorIsClosing);
					this.interval();
					this.disabledButton = false;
				});
			}, err => {
				this.disabledButton = false;
			});
		});
	}

	closeDoorRequestForShare() {
		this.loader.show().then(() => {
			this.shareService.closeBox1({ shareId: this.booking.shareId }).subscribe(r => {
				this.bookingHelper.removeBookingAtTheGate();
				this.loader.hide().then(() => {
					this.action(BookingStatus.DoorIsClosing);
					this.interval();
					this.disabledButton = false;
				});
			}, err => {
				this.disabledButton = false;
			});
		});
	}

	private checkDistance() {
		this.geolocationService.calculateDistance(this.group.geoLat, this.group.geoLng).subscribe(d => {
			if (d.d <= this.group.userDistance) {
				if (this.booking.sharedWithMe) {
					this.closeDoorRequestForShare();
				} else {
					this.closeDoorRequest();
				}
			} else {
				this.alertService.distanceAlert('distance-close', this.group.userDistance);
				this.disabledButton = false;
			}
		}, err => {
			this.helperService.locationPermission();
			this.loader.hide();
		});
	}

	async abort(towerStatus?: string) {
		const data: any = { fromAbort: true };
		if (towerStatus) {
			data.towerStatus = towerStatus;
		}
		this.closeAlert();
		await this.bookingHelper.closeDoorModal(data);
		this.stopInterval(false);
	}

	closeAlert() {
		this.alertCtrl.getTop().then(async v => {
			if(v) await this.alertCtrl.dismiss();
		});
	}

	refresh() {
		if (this.booking.sharedWithMe) {
			this.checkStatusRequestForShare();
		} else {
			this.checkStatusRequest();
		}
	}

	stopInterval(reinstall: boolean) {
		this.notifier.next('');
		this.notifier.complete();
		this.abortIntervalExpired = 0;
		this.showAbortBtn = true;

		if (reinstall) {
			this.notifier = new Subject<any>();
		}
	}

	stopStatusNotifier() {
		this.statusNotifier.next('');
		this.statusNotifier.complete();
	}
}
